/**
 * Shared dashboard stat-card primitives.
 *
 * One source of truth for the dashboard's stat-tile language so every card —
 * the generic MetricCard and the specialized Overdue / Collection / On-Time
 * cards — reads as one cohesive system: the same hairline frame, the same
 * hero-number treatment, the same real-color-tint category icon.
 *
 *   StatCardFrame — the surface: hairline border, soft-on-hover lift,
 *   focus-visible ring, reduced-motion aware. Becomes a real button when an
 *   ariaLabel is supplied (so cards with nested controls can opt out and stay
 *   plain clickable, avoiding nested-button semantics).
 *
 *   StatCardHead — the header block: quiet label (+ optional info affordance),
 *   the hero value, an optional subtitle, and the tinted category icon.
 */

import React from "react";
import { Box, Card, Typography, Tooltip } from "@mui/material";
import { alpha, SxProps, Theme } from "@mui/material/styles";
import { InfoOutlined as InfoIcon } from "@mui/icons-material";

/** Semantic palette keys a card can tint its icon/accent with. */
export type MetricColor =
  | "primary"
  | "secondary"
  | "success"
  | "error"
  | "warning"
  | "info";

interface StatCardFrameProps {
  onClick?: () => void;
  /**
   * When provided together with onClick, the frame exposes real button
   * semantics (role, tabIndex, Enter/Space). Omit for cards that contain their
   * own nested interactive controls to avoid nested-button semantics.
   */
  ariaLabel?: string;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

export const StatCardFrame: React.FC<StatCardFrameProps> = ({
  onClick,
  ariaLabel,
  children,
  sx,
}) => {
  const asButton = !!onClick && !!ariaLabel;
  return (
    <Card
      {...(asButton && {
        role: "button",
        tabIndex: 0,
        "aria-label": ariaLabel,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick?.();
          }
        },
      })}
      onClick={onClick}
      sx={[
        (theme) => ({
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: 2.5,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "none",
          cursor: onClick ? "pointer" : "default",
          transition: theme.transitions.create(
            ["transform", "box-shadow", "border-color"],
            { duration: theme.transitions.duration.shorter },
          ),
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          "&:hover": onClick
            ? {
                transform: "translateY(-3px)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
                borderColor: alpha(theme.palette.primary.main, 0.4),
                "@media (prefers-reduced-motion: reduce)": {
                  transform: "none",
                },
              }
            : {},
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2,
          },
        }),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {children}
    </Card>
  );
};

interface StatCardHeadProps {
  label: string;
  tooltip?: React.ReactNode;
  icon: React.ReactElement;
  color: MetricColor;
  value: React.ReactNode;
  /** sx color token for the value; defaults to text.primary. */
  valueColor?: string;
  subtitle?: React.ReactNode;
}

export const StatCardHead: React.FC<StatCardHeadProps> = ({
  label,
  tooltip,
  icon,
  color,
  value,
  valueColor = "text.primary",
  subtitle,
}) => (
  <>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 1,
        mb: 1.25,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
        <Typography
          // A single word ("Outstanding") has no break opportunity, so wrap
          // alone cannot save it in a narrow card; the title keeps the full
          // label reachable. Not when the card carries an info tooltip: two
          // tooltips on one card head, one native and one styled, read as a
          // glitch (change review 2026-09-15, portion 5).
          title={tooltip ? undefined : label}
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: "text.secondary",
            letterSpacing: 0.2,
            lineHeight: 1.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            // Wrap on a phone-width screen instead of truncating. Reflow at
            // 320px must not cost information (WCAG 1.4.10), and an ellipsis
            // with nothing to reveal it turned these into "Outstan…",
            // "Collectio…", "On-Time…".
            whiteSpace: { xs: "normal", sm: "nowrap" },
          }}
        >
          {label}
        </Typography>
        {tooltip && (
          <Tooltip
            // The label leads the tooltip, so a truncated label on a narrow
            // desktop card is still readable in full from the icon (the
            // native title on the text was dropped to avoid two tooltips).
            title={
              <>
                <Box component="span" sx={{ fontWeight: 600, display: "block" }}>
                  {label}
                </Box>
                {tooltip}
              </>
            }
            arrow
            placement="top"
          >
            <InfoIcon
              role="img"
              // Meaningful (labeled) + focusable, so override SvgIcon's default
              // aria-hidden — otherwise it's an aria-hidden focusable element.
              aria-hidden={false}
              aria-label={
                typeof tooltip === "string"
                  ? `About ${label}: ${tooltip}`
                  : `About ${label}`
              }
              tabIndex={0}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              sx={{
                fontSize: 13,
                color: "text.secondary",
                cursor: "help",
                flexShrink: 0,
                "&:hover": { color: "text.primary" },
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  borderRadius: "50%",
                },
              }}
            />
          </Tooltip>
        )}
      </Box>
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 36,
          height: 36,
          borderRadius: 2,
          flexShrink: 0,
          bgcolor: alpha(theme.palette[color].main, 0.12),
          color: `${color}.main`,
        })}
      >
        {React.cloneElement(icon as React.ReactElement<{ sx?: object }>, {
          sx: { fontSize: 20 },
        })}
      </Box>
    </Box>
    {/* Word gaps between the label, value and subtitle blocks. JSX drops the
        whitespace HTML would have, so the DOM text read "Students125Active"
        and a clickable card's "Students, 125, Active" label no longer
        contained it (axe label-content-name-mismatch, WCAG 2.5.3). A
        whitespace-only text node in a flex column renders nothing. */}
    {" "}
    <Typography
      component="div"
      sx={{
        fontWeight: 700,
        color: valueColor,
        lineHeight: 1.1,
        fontVariantNumeric: "tabular-nums",
        fontSize: { xs: "1.4rem", sm: "1.5rem", md: "1.7rem" },
      }}
    >
      {value}
    </Typography>
    {subtitle && " "}
    {subtitle && (
      <Box
        sx={{
          mt: 0.5,
          fontSize: "0.75rem",
          color: "text.secondary",
          lineHeight: 1.35,
        }}
      >
        {subtitle}
      </Box>
    )}
  </>
);
